/* ══════════════════════════════════════════════════════════════════════════
   YCM lens logic — contact lens power panel + spectacle lens flow.

   Everything that stops a bad order lives here. The Liquid only draws.

   THE RULES, and why each exists:

   1  An unticked eye is DISABLED, not hidden. Hiding it leaves its values in
      the DOM and they end up on the order; disabling removes it from
      validation, from the total and from the properties. It also stays on
      screen so the shopper can see what they turned off.
   2  At least one eye must be ticked. Nobody buys lenses for no eyes.
   3  Every field the lens design needs must be filled for every ticked eye.
      Toric needs CYL and AXIS together — a cylinder without an axis is not a
      prescription, it is half of one.
   4  Typed powers are checked against a real dioptre pattern and a sane range.
      Dropdown powers cannot be wrong because there is nothing else to pick.
   5  Axis is a whole number 0–180. 181 is not a direction.
   6  The first bad field gets focus. An error message nobody can find is not
      an error message.
   7  Prices are read from the store, never from the theme.

   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ── helpers ─────────────────────────────────────────────────────────── */

  // The approved design shows "₹ 1,500", not "₹ 1,500.00". Lens prices are whole
  // rupees, and trailing .00 on every line makes the bill look like a bank
  // statement rather than a price. Paise are shown only if a price ever has any.
  var money = function (paise) {
    var n = (paise || 0) / 100;
    var frac = n % 1 === 0 ? 0 : 2;
    return '₹ ' + n.toLocaleString('en-IN', { minimumFractionDigits: frac,
                                              maximumFractionDigits: frac });
  };

  // A dioptre: optional sign, digits, optional .25/.50/.75/.00. Accepts "-2.25",
  // "+1", "0.00", "2.5". Rejects "abc", "--1", "2.3" (not a quarter step).
  var DIOPTRE = /^[+-]?\d{1,2}(\.(00|25|50|75|0|5))?$/;

  function normPower(raw) {
    var s = String(raw || '').trim().replace(/\s+/g, '').replace(/,/g, '.');
    if (!s) return null;
    if (!DIOPTRE.test(s)) return null;
    var n = parseFloat(s);
    if (!isFinite(n) || Math.abs(n) > 25) return null;      // no lens is ±25
    // quarter-step check catches "2.3" that slipped the regex via "2.3"
    if (Math.abs(Math.round(n * 4) - n * 4) > 1e-9) return null;
    return (n > 0 ? '+' : n < 0 ? '-' : '') + Math.abs(n).toFixed(2);
  }

  // ⚠️ AXIS RUNS 1 TO 180, NOT 0 TO 180. It is a direction on a half-circle
  // measured anticlockwise from the horizontal, and the horizontal itself is
  // written 180 — 0 is simply not a value an optician writes. Accepting it let
  // a prescription through that no lab would grind.
  function normAxis(raw) {
    var s = String(raw || '').trim();
    if (!s) return null;
    if (!/^\d{1,3}$/.test(s)) return null;
    var n = parseInt(s, 10);
    if (n < 1 || n > 180) return null;
    return String(n);
  }

  /* ⚠️ AN AXIS WITHOUT A CYLINDER IS NOT A PRESCRIPTION — it is a data-entry
     accident, and it shipped.

     The axis is the ORIENTATION of the cylinder. With no cylinder there is
     nothing to orient, so "CYL 0.00, Axis 67" is meaningless; a lab receiving
     it would have to ring the customer. The old rule paired the two fields by
     EMPTINESS only, and `0.00` is a real, selectable cylinder value meaning
     "no astigmatism" — a non-empty string. So picking 0.00 and typing an axis
     passed validation cleanly.

     Hence: parse the cylinder, never test the string for truthiness. */
  function cylIsReal(el) {
    var v = normPower((el && el.value) || '');
    return v !== null && parseFloat(v) !== 0;
  }

  function fail(box, msg, field) {
    if (box) { box.textContent = msg; box.hidden = false; }
    if (field) {
      try { field.focus({ preventScroll: false }); } catch (e) { field.focus(); }
      field.classList.add('is-bad');
      field.addEventListener('input', function h() {
        field.classList.remove('is-bad'); field.removeEventListener('input', h);
      });
      field.addEventListener('change', function h() {
        field.classList.remove('is-bad'); field.removeEventListener('change', h);
      });
    }
    return false;
  }
  function clearErr(box) { if (box) { box.hidden = true; box.textContent = ''; } }

  /* ══ THE CART, AND THE ONE-ORDER CHECKOUT ════════════════════════════════ */

  function cartRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /* -- is Razorpay Magic Checkout actually live on this page? ---------------
     The app embed prints its own config into the page. Reading it is the only
     honest test: the app can be installed but not live, and the theme must not
     hand a shopper to a checkout that is not there.

     ⚠️ Gate the hand-off on this, never assume. If Razorpay is ever removed,
     paused, or fails to boot, every Buy it now on the store must fall straight
     back to Shopify's own /checkout rather than parking the shopper on a cart
     page waiting for a modal that will never open.

     ⚠️ THE APP SHIPS MORE THAN ONE INTEGRATION AND THEY DO NOT SHARE A NAME.
     The older one (magic-sopc.js) printed #RazorpayMagicSopcConfig holding a
     flat {status}. The one installed here (magic-shopify.js, snippet
     snippets/razorpay-magic-checkout.liquid) prints #RazorpayMagicCheckoutConfig
     holding {config:{productConfig:{status}, cartConfig:{...}}} -- a different
     id AND a different shape, so the old probe returned false on every page and
     every Buy it now quietly went to Shopify's checkout instead. Nothing errors;
     the button simply works and Magic Checkout never opens. Read the id and the
     shape off the snippet on the LIVE theme, never from memory. */
  function magicCheckoutLive() {
    if (typeof window.openRzpCheckout !== 'function') return false;
    try {
      var el = document.getElementById('RazorpayMagicCheckoutConfig');
      if (!el) return false;
      var cfg = JSON.parse(el.textContent || '{}').config || {};
      return (cfg.productConfig || {}).status === 'live';
    } catch (e) { return false; }
  }

  /* -- open Magic Checkout over the cart we just built ----------------------
     Razorpay's bundle ends by copying its exports onto the global object, so
     window.openRzpCheckout() is a real, callable entry point. It takes no
     arguments (the click event it is normally handed is optional throughout),
     fetches /cart.js itself, and opens the modal over whatever it finds -- which
     is exactly the frame + lens order this flow has just assembled.

     ⚠️ DO NOT INSTEAD POINT THEIR "custom selector" SETTING AT OUR BUTTON.
     Every one of their handlers -- productCustom included -- calls
     stopImmediatePropagation() from the CAPTURE phase, so it runs BEFORE our
     listener: the prescription would never be validated and the second line item
     would never be added.

     ⚠️ AND IT CAN SILENTLY DO NOTHING. Their script sets an internal kill flag
     for a non-INR currency or a shopper outside India, after which this call
     returns without opening anything and without saying so. A dead Buy it now is
     worse than a plain one, so watch for their modal and fall back to Shopify's
     checkout if it never arrives.

     ⚠️ TEST THE MODAL'S display, NOT WHETHER ITS ELEMENTS EXIST. Razorpay mounts
     .razorpay-container AND .razorpay-checkout-frame into every page at load,
     hidden — so `querySelector('.razorpay-container')` is true on a page nobody
     has clicked, and a fallback gated on it can never fire. Measured on the live
     store: at rest display is `none` and only `block` once the modal is really
     open (they also add .razorpay-container-visible, but the computed display is
     the verdict and cannot be renamed out from under us). */
  function magicModalOpen() {
    var c = document.querySelector('.razorpay-container');
    return !!c && getComputedStyle(c).display !== 'none';
  }

  function openMagicOr(fallback) {
    window.openRzpCheckout();
    var waited = 0;
    var poll = setInterval(function () {
      if (magicModalOpen()) {
        clearInterval(poll);
      } else if ((waited += 250) >= 4000) {
        clearInterval(poll);
        window.location = fallback;
      }
    }, 250);
  }

  // every cart endpoint speaks JSON both ways. ⚠️ /cart/clear.js answers 411
  // "Length Required" to a POST carrying no body at all, so always send one.
  function jcart(url, body, method) {
    return fetch(url, {
      method: method || 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body == null ? undefined : JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(
          function (j) { throw new Error(j.description || j.message || 'Cart error'); },
          function () { throw new Error('Cart error ' + r.status); });
      }
      return r.json();
    });
  }

  /* -- "Buy it now" must take ONLY what the shopper just configured ---------
     Client, 2026-08-18, reported twice -- once for spectacle lenses and again
     for contact lenses: "it should only take that lens + frame to checkout, not
     the whole cart".

     ⚠️ SHOPIFY CANNOT BUILD A CHECKOUT BESIDE THE CART. /checkout IS the cart,
     and the two obvious escapes are both dead ends:
       - a /cart/<variant>:<qty> PERMALINK does reach a one-item checkout, but it
         REPLACES the cart anyway (measured against the live store, not assumed)
         AND it cannot carry line-item properties at all -- so the prescription,
         the one thing an optician must never lose, would simply be dropped.
       - the native dynamic checkout button posts ONE variant from ONE form, so
         it can express neither a frame plus a lens nor a power step. That is
         why it is not rendered on either of these products.

     So the cart IS replaced, exactly as Shopify's own permalinks do it -- and
     the shopper's other lines are parked in localStorage first. assets/ycm.js
     puts them back the next time they land on any page of the store, whether
     they completed the order or abandoned it. Nothing is lost either way.

     `doAdd` is a function rather than a list of items because the two callers
     add differently: the spectacle flow posts JSON, the contact lens flow posts
     a FormData so an uploaded prescription file can ride along. */
  var BAG_STASH = 'ycm:bag';

  function checkoutWithOnly(doAdd) {
    var root = cartRoot(), stashed = false;
    return jcart(root + 'cart.js', null, 'GET')
      .then(function (c) {
        var bag = (c.items || []).map(function (i) {
          var line = { id: i.variant_id, quantity: i.quantity };
          if (i.properties && Object.keys(i.properties).length) line.properties = i.properties;
          return line;
        });
        if (!bag.length) return null;
        try { localStorage.setItem(BAG_STASH, JSON.stringify(bag)); stashed = true; } catch (e) {}
        return jcart(root + 'cart/clear.js', {});
      })
      .then(function () { return doAdd(); })
      .then(function () {
        /* ⚠️ RAZORPAY MAGIC CHECKOUT CANNOT BE REACHED BY GOING TO /checkout.
           Magic Checkout is a modal opened by Razorpay's own script on a
           STOREFRONT page; /checkout is Shopify's hosted checkout, where that
           script does not run at all. So the hand-off happens right here, on the
           product page, over the cart this function has just built.

           Razorpay only ever binds itself to Shopify's OWN buttons -- the native
           dynamic checkout button on a product page and button[name="checkout"]
           on the cart. This page renders neither: a lens order is two line items
           carrying a prescription, which the native button cannot express. That
           is why Magic Checkout worked on sunglasses and on nothing else, and why
           no Razorpay-branded button appeared on eyeglasses or contact lenses --
           there was nothing on the page for it to attach to. Calling their own
           entry point is the supported way in for a custom flow.

           Nothing is navigated to, so the parked bag stays parked: assets/ycm.js
           restores it on the shopper's next page load, whether they completed the
           order or closed the modal. Both outcomes are what a shopper expects. */
        if (magicCheckoutLive()) { return openMagicOr(root + 'checkout'); }
        window.location = root + 'checkout';
      })
      .catch(function (e) {
        // ⚠️ the add failed AFTER the cart was emptied. Put the bag back here and
        // now rather than leaving it to the next page load, because the shopper
        // is still standing on this one looking at an error.
        if (!stashed) throw e;
        var raw; try { raw = localStorage.getItem(BAG_STASH); } catch (x) {}
        if (!raw) throw e;
        try { localStorage.removeItem(BAG_STASH); } catch (x) {}
        return jcart(root + 'cart/add.js', { items: JSON.parse(raw) })
          .then(function () { throw e; }, function () { throw e; });
      });
  }

  /* ══════════════════════════════════════════════════════════════════════
     CONTACT LENS POWER PANEL
     ══════════════════════════════════════════════════════════════════════ */

  function initPower(root) {
    var err     = root.querySelector('[data-pw-err]');
    var design  = root.dataset.design;
    var needCyl = root.dataset.needCyl === 'true';
    var needAdd = root.dataset.needAdd === 'true';
    var cosmetic = root.dataset.cosmetic === 'true';
    var price   = parseInt(root.dataset.price, 10) || 0;
    var body    = root.querySelector('.ycm-pw__body');
    var withPower = !cosmetic;                   // cosmetic starts on "Without"

    var f = function (sel) { return root.querySelectorAll(sel); };
    var mode = function () {
      var m = root.querySelector('input[name^="pwmode-"]:checked');
      return m ? m.value : 'manual';
    };
    var fields = function (eye) {
      return Array.prototype.filter.call(f('[data-pw][data-eye="' + eye + '"]'),
        function (el) { return el.dataset.pw !== 'boxes'; });
    };
    var boxSel = function (eye) {
      return root.querySelector('[data-pw="boxes"][data-eye="' + eye + '"]');
    };
    var eyeOn = function (eye) {
      var c = root.querySelector('[data-eye-on="' + eye + '"]');
      return !c || c.checked;
    };

    /* ── the supplier's sphere x cylinder -> axis table ───────────────────
       Rows, not lists. See snippets/ycm-lens-power.liquid for why, and
       stores/ycm-opticals/lens-power-combos.json for the rows themselves. */
    var combos = null, comboCyl = [], comboAxis = [];
    (function () {
      var el = root.querySelector('[data-ycm-combos]');
      if (!el) return;
      try {
        var rows = (JSON.parse(el.textContent) || {}).rows;
        if (rows && rows.length) {
          combos = rows;
          rows.forEach(function (r) {
            (r.cyl || []).forEach(function (v) { if (comboCyl.indexOf(v) < 0) comboCyl.push(v); });
            (r.axis || []).forEach(function (v) { if (comboAxis.indexOf(v) < 0) comboAxis.push(v); });
          });
          comboAxis.sort(function (a, b) { return parseInt(a, 10) - parseInt(b, 10); });
        }
      } catch (e) { combos = null; }   // a broken payload must not break the panel
    }());

    /* Replace a select's options, keeping the current value when it survives.
       ⚠️ Assigning .value never fires `change`, so this cannot recurse into
       syncEyes — but it DOES silently blank a value that is no longer offered,
       which is the point: an axis that the chosen cylinder does not have must
       not stay selected just because it was legal a moment ago. */
    function setOptions(sel, values) {
      if (!sel) return;
      var keep = sel.value;
      var first = sel.options[0];
      var ph = (first && first.value === '') ? first.text : 'Select';
      var html = '<option value="">' + ph + '</option>';
      for (var i = 0; i < values.length; i++) {
        html += '<option value="' + values[i] + '">' + values[i] + '</option>';
      }
      sel.innerHTML = html;
      sel.value = values.indexOf(keep) >= 0 ? keep : '';
    }

    function rowsFor(sphV, cylV) {
      return combos.filter(function (r) {
        if (sphV && (r.sph || []).indexOf(sphV) < 0) return false;
        if (cylV && (r.cyl || []).indexOf(cylV) < 0) return false;
        return true;
      });
    }
    function unionOf(rows, key) {
      var out = [];
      rows.forEach(function (r) {
        (r[key] || []).forEach(function (v) { if (out.indexOf(v) < 0) out.push(v); });
      });
      return out;
    }

    /* Narrow ONE eye's cylinder and axis to what the table actually offers for
       the sphere (and then the cylinder) that eye has chosen. Kept separate from
       syncEyes so `mirror()` can re-run it after copying right onto left — a
       mirrored sphere changes which cylinders the left eye may have. */
    function narrowEye(eye) {
      if (!combos) return;
      var sphEl = root.querySelector('[data-pw="sph"][data-eye="' + eye + '"]');
      var cylEl = root.querySelector('[data-pw="cyl"][data-eye="' + eye + '"]');
      var axEl = root.querySelector('[data-pw="axis"][data-eye="' + eye + '"]');
      var sphV = sphEl ? String(sphEl.value || '') : '';
      if (cylEl) {
        setOptions(cylEl, sphV ? unionOf(rowsFor(sphV, ''), 'cyl') : comboCyl);
      }
      if (axEl) {
        var cylV = cylEl ? String(cylEl.value || '') : '';
        setOptions(axEl, (sphV && cylV) ? unionOf(rowsFor(sphV, cylV), 'axis') : comboAxis);
      }
    }

    /* ── rule 1: an unticked eye is disabled, dimmed, and out of the sums ── */
    function syncEyes() {
      ['r', 'l'].forEach(function (eye) {
        var on = eyeOn(eye);
        fields(eye).concat([boxSel(eye)]).forEach(function (el) {
          if (!el) return;
          el.disabled = !on;
          el.classList.toggle('is-off', !on);
          if (!on) el.classList.remove('is-bad');
        });
        /* ── the CASCADE: sphere, then cylinder, then axis ─────────────────
           Client, 2026-08-19: "make them choose mandatory power first like
           sphere and then unlock the cylinderical then axis".

           Sphere is the only always-required field, and a cylinder or an axis
           without one is not a prescription — it is a half-filled form that
           reaches the lab. Locking each field until the one it depends on is
           answered makes the wrong order unreachable rather than merely
           rejected, which is the same reasoning as the axis/cylinder rule
           below and the reason the client can confirm it just by looking.

           ⚠️ Every lock CLEARS the field it locks. A value left behind in a
           disabled select still submits on some browsers, and worse, it is
           invisible to the shopper — they would be buying a cylinder they can
           no longer see. */
        var sphEl = root.querySelector('[data-pw="sph"][data-eye="' + eye + '"]');
        var sphOn = !sphEl || String(sphEl.value || '').trim() !== '';

        /* narrow BEFORE the locks are applied, so a value the table no longer
           offers is already gone when we decide whether the axis has a real
           cylinder to orient */
        narrowEye(eye);

        var cylEl = root.querySelector('[data-pw="cyl"][data-eye="' + eye + '"]');
        if (cylEl) {
          var cylOn = on && sphOn;
          cylEl.disabled = !cylOn;
          cylEl.classList.toggle('is-off', !cylOn);
          if (!cylOn) { cylEl.value = ''; cylEl.classList.remove('is-bad'); }
        }
        /* ⚠️ and the axis needs a real cylinder on top of a ticked eye -- see
           cylIsReal(). A toric lens whose cylinder list offers 0.00 would
           otherwise let an axis through with nothing to orient. */
        var axEl = root.querySelector('[data-pw="axis"][data-eye="' + eye + '"]');
        if (cylEl && axEl) {
          var axOn = on && sphOn && cylIsReal(cylEl);
          axEl.disabled = !axOn;
          axEl.classList.toggle('is-off', !axOn);
          if (!axOn) { axEl.value = ''; axEl.classList.remove('is-bad'); }
        }
        var addEl = root.querySelector('[data-pw="add"][data-eye="' + eye + '"]');
        if (addEl) {
          var addOn = on && sphOn;
          addEl.disabled = !addOn;
          addEl.classList.toggle('is-off', !addOn);
          if (!addOn) { addEl.value = ''; addEl.classList.remove('is-bad'); }
        }
      });
      var same = root.querySelector('[data-pw-same]');
      if (same) {
        // "same in both eyes" is meaningless with one eye
        var both = eyeOn('r') && eyeOn('l');
        same.disabled = !both;
        same.closest('.ycm-cbx').classList.toggle('is-off', !both);
        if (!both) same.checked = false;
      }
      mirror();
      total();
    }

    /* ── "same power in both eyes" keeps left locked to right ───────────── */
    function mirror() {
      var same = root.querySelector('[data-pw-same]');
      if (!same || !same.checked) {
        fields('l').forEach(function (el) { el.classList.remove('is-mirror'); });
        return;
      }
      /* ⚠️ COPY IN DEPENDENCY ORDER AND RE-NARROW BETWEEN STEPS. Assigning a
         value a <select> does not currently offer silently blanks it — so
         copying the right eye's cylinder before the left eye's option list has
         been rebuilt for the newly copied sphere loses it, and "same in both
         eyes" quietly produces two different prescriptions. Sphere decides the
         cylinder list; cylinder decides the axis list. */
      ['sph', 'cyl', 'axis', 'add'].forEach(function (k) {
        var src = root.querySelector('[data-pw="' + k + '"][data-eye="r"]');
        var dst = root.querySelector('[data-pw="' + k + '"][data-eye="l"]');
        if (!src || !dst) return;
        if (k === 'cyl' || k === 'axis') narrowEye('l');
        dst.value = src.value;
        dst.classList.add('is-mirror');
      });
    }

    function total() {
      var n = 0;
      ['r', 'l'].forEach(function (eye) {
        if (!eyeOn(eye)) return;
        var b = boxSel(eye);
        n += b ? (parseInt(b.value, 10) || 0) : 1;
      });
      var el = root.querySelector('[data-pw-total]');
      if (el) el.textContent = money(price * n);
      root.dataset.boxes = n;
    }

    /* ── validation ─────────────────────────────────────────────────────── */
    function validate() {
      clearErr(err);
      if (cosmetic && !withPower) return { ok: true, props: { 'Power': 'Without power' } };

      var m = mode();
      if (m === 'upload') {
        var file = root.querySelector('[data-pw-file]');
        if (!file || !file.files || !file.files.length) {
          return { ok: fail(err, 'Please choose your prescription file, or switch to ' +
                                 '“Enter my power”.', file) };
        }
        return { ok: true, props: { 'Power': 'See uploaded prescription' } };
      }

      if (!eyeOn('r') && !eyeOn('l')) {
        return { ok: fail(err, 'Tick Right, Left, or both — we need at least one eye.',
                          root.querySelector('[data-eye-on="r"]')) };
      }

      var props = {}, bad = null, msg = '';
      ['r', 'l'].forEach(function (eye) {
        if (bad || !eyeOn(eye)) return;
        var label = eye === 'r' ? 'Right eye' : 'Left eye';
        var parts = [];

        var wanted = ['sph'];
        // axis only where the cylinder is real -- see cylIsReal()
        if (needCyl) {
          wanted.push('cyl');
          var cylProbe = root.querySelector('[data-pw="cyl"][data-eye="' + eye + '"]');
          if (cylIsReal(cylProbe)) wanted.push('axis');
        }
        if (needAdd) wanted.push('add');

        for (var i = 0; i < wanted.length; i++) {
          var key = wanted[i];
          var el = root.querySelector('[data-pw="' + key + '"][data-eye="' + eye + '"]');
          if (!el) continue;
          var raw = (el.value || '').trim();
          if (!raw) {
            bad = el;
            msg = label + ': choose ' + ({ sph: 'a power', cyl: 'a cylinder',
                    axis: 'an axis', add: 'an add power' })[key] + '.';
            return;
          }
          // ⚠️ A value picked from a SELECT is valid by construction — it came
          // out of the supplier's own profile. Only TYPED values need checking.
          // This also lets a supplier express a band rather than values, which
          // CooperVision does for one multifocal: "LOW (up to +2.25)".
          var val;
          if (el.tagName === 'SELECT') {
            val = raw;
          } else {
            val = key === 'axis' ? normAxis(raw) : normPower(raw);
            if (val === null) {
              bad = el;
              msg = key === 'axis'
                ? label + ': axis is a whole number from 1 to 180.'
                : label + ': “' + raw + '” is not a power. Use a number like -2.25.';
              return;
            }
          }
          parts.push(({ sph: 'SPH', cyl: 'CYL', axis: 'Axis', add: 'ADD' })[key] + ' ' + val);
        }

        var b = boxSel(eye);
        var n = b ? parseInt(b.value, 10) : 1;
        if (!n || n < 1) { bad = b; msg = label + ': choose how many boxes.'; return; }
        props[label] = parts.join('  ') + '  ×' + n + (n > 1 ? ' boxes' : ' box');
      });

      if (bad) return { ok: fail(err, msg, bad) };
      if (!Object.keys(props).length) {
        return { ok: fail(err, 'Please fill in your power.') };
      }
      return { ok: true, props: props };
    }

    /* ── wire ───────────────────────────────────────────────────────────── */
    root.addEventListener('change', function (e) {
      /* ⚠️ Re-sync from the fields the cascade DEPENDS ON, not only from the
         cylinder. A sphere change unlocks (or re-locks) the cylinder, the add
         and — through the cylinder — the axis; without this line the sphere
         gate would be evaluated once at boot and never again, which is exactly
         how the earlier axis guard shipped stuck in the disabled state and made
         a real astigmatism unorderable. */
      if (e.target.matches('[data-pw="sph"],[data-pw="cyl"]')) syncEyes();
      if (e.target.matches('[data-eye-on]')) syncEyes();
      else if (e.target.matches('[data-pw-same]')) mirror();
      else if (e.target.matches('[data-pw][data-eye="r"]')) { mirror(); total(); }
      else if (e.target.matches('[data-pw="boxes"]')) total();
      else if (e.target.matches('input[name^="pwmode-"]')) {
        var m = e.target.value;
        root.querySelectorAll('[data-pw-pane]').forEach(function (p) {
          p.hidden = p.dataset.pwPane !== m;
        });
        clearErr(err);
      }
      if (e.target.matches('[data-pw][data-eye="l"]')) {
        var same = root.querySelector('[data-pw-same]');
        if (same && same.checked) { same.checked = false; mirror(); }
      }
    });
    root.addEventListener('input', function (e) {
      if (e.target.matches('[data-pw="axis"]')) {
        var raw = (e.target.value || '').trim();
        if (raw && normAxis(raw) === null) {
          e.target.classList.add('is-bad');
          fail(err, 'Axis is a whole number from 1 to 180.');
        } else { e.target.classList.remove('is-bad'); clearErr(err); }
      }
      if (e.target.matches('[data-pw][data-eye="r"]')) mirror();
    });

    // cosmetic: With / Without power
    root.querySelectorAll('[data-pw-type]').forEach(function (b) {
      b.addEventListener('click', function () {
        withPower = b.dataset.pwType === 'power';
        root.querySelectorAll('[data-pw-type]').forEach(function (x) {
          x.setAttribute('aria-pressed', String(x === b));
        });
        if (body) body.hidden = !withPower;
        clearErr(err);
      });
    });

    // A separate quantity stepper next to "boxes per eye" is two controls for
    // one number, and they disagree. The panel owns the quantity here.
    // ⚠️ getElementById, NOT querySelector. The section id is like
    // "template--22359572119806__main", and "#ycm-qty-template--2235..." is not
    // a parseable CSS selector — querySelector throws or misses, so the stepper
    // stayed on screen next to the per-eye box counts: two controls for one
    // number, disagreeing with each other.
    var sid = (root.id || '').replace('ycm-pw-', '');
    var qtyBox = document.getElementById('ycm-qty-' + sid);
    var qtyWrap = qtyBox && qtyBox.closest('.ycm-qty');
    if (qtyWrap) { qtyWrap.hidden = true; qtyWrap.style.display = 'none'; }

    syncEyes();
    root._ycmValidate = validate;
    return validate;
  }

  /* ── hook the product form so nothing invalid can be submitted ───────── */
  function guardForm(panel) {
    if (!panel) return;
    // ⚠️ There is more than one cart form on the page. Dawn renders a second
    // one for the dynamic checkout button, and querySelector returns only the
    // first — so Buy it now was completely unguarded: one click and a lens
    // shipped with no prescription at all. Guard every cart form, and remove
    // the express button entirely on a product that must collect a power,
    // because a one-click checkout cannot carry the power step.
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    if (!forms.length) return;
    var needsPower = panel.dataset.entry !== 'None' || panel.dataset.cosmetic === 'true';
    if (needsPower) {
      document.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]')
        .forEach(function (b) {
          var host = b.closest('form') || b;
          host.hidden = true; host.style.display = 'none';
        });
    }
    Array.prototype.forEach.call(forms, function (form) { bind(form); });

    /* Writes the validated power onto the form as line-item properties. Shared
       by the ordinary submit and by the "Buy it now" button below, so the two
       routes can never disagree about what reaches the order. */
    function stamp(form, res) {
      form.querySelectorAll('[data-pw-prop]').forEach(function (n) { n.remove(); });
      Object.keys(res.props).forEach(function (k) {
        var i = document.createElement('input');
        i.type = 'hidden'; i.name = 'properties[' + k + ']';
        i.value = res.props[k]; i.setAttribute('data-pw-prop', '');
        form.appendChild(i);
      });
      // the file input must live INSIDE the form for Shopify to accept it
      var file = panel.querySelector('[data-pw-file]');
      if (file && file.files && file.files.length && !file.name) {
        file.name = 'properties[Prescription]';
        form.appendChild(file);
      }
      // ⚠️ The PDP has its OWN submit handler that copies the visible stepper
      // into the hidden quantity field, and it runs after this one -- so setting
      // only the hidden field is silently undone and every order arrives as 1.
      // Set the stepper too, which is the value that actually survives.
      var boxes = parseInt(panel.dataset.boxes, 10);
      if (boxes > 0) {
        var q = form.querySelector('[name="quantity"]');
        if (q) q.value = boxes;
        var stepper = document.getElementById('ycm-qty-' + (panel.id || '').replace('ycm-pw-', ''));
        if (stepper) stepper.value = boxes;
      }
    }

    function bind(form) {
      form.addEventListener('submit', function (e) {
        var res = panel._ycmValidate();
        if (!res.ok) { e.preventDefault(); e.stopPropagation(); return; }
        stamp(form, res);
      }, true);
    }

    /* ⚠️ CONTACT LENSES HAD NO EXPRESS ROUTE AT ALL. The native dynamic
       checkout button cannot carry the power step, so it was removed -- and
       nothing replaced it, which the client noticed ("there is no buy it now
       button in contact lens", 2026-08-18). This is the replacement: same
       validation as Add to cart, then a checkout holding only this line.
       FormData rather than JSON because an uploaded prescription is a FILE, and
       /cart/add.js takes it only that way. */
    var nowBtn = document.querySelector('[data-pw-buy]');
    var form0 = forms[0];
    if (nowBtn && form0) {
      var errBox = panel.querySelector('[data-pw-err]');
      nowBtn.addEventListener('click', function () {
        var res = panel._ycmValidate();
        if (!res.ok) return;                       // _ycmValidate already reported
        stamp(form0, res);
        nowBtn.disabled = true;
        var add = function () {
          return fetch(cartRoot() + 'cart/add.js', {
            method: 'POST', headers: { Accept: 'application/json' },
            body: new FormData(form0)
          }).then(function (r) {
            if (!r.ok) {
              return r.json().then(
                function (j) { throw new Error(j.description || 'Could not add to cart'); },
                function () { throw new Error('Could not add to cart'); });
            }
            return r.json();
          });
        };
        checkoutWithOnly(add).catch(function (e) {
          nowBtn.disabled = false;
          fail(errBox, e.message || 'Could not start checkout. Please try again.');
        });
      });
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
     SPECTACLE LENS FLOW
     ══════════════════════════════════════════════════════════════════════ */

  function initRx(root) {
    // The buy box owns the error line now — the rx panel no longer has its own
    // footer. Falls back to a panel-local one so the snippet stays reusable.
    var buy = root.closest('[data-ycm-buy]');
    /* TWO error surfaces, on purpose. `rxErr` sits inside the panel, under the
       fields -- that is where a bad power or axis is reported, next to the
       control that caused it. `err` is the buy box's own, for cart and checkout
       failures, which belong beside the button that triggered them. */
    var rxErr = root.querySelector('[data-rx-err]');
    var err = (buy || root).querySelector('[data-buy-err]') || rxErr;
    if (!rxErr) rxErr = err;
    // ⚠️ the default side of the switch is a THEME SETTING now, not a constant
    // (client, 2026-08-18: "give me option to set which is the default option").
    // The Liquid stamps it on [data-ycm-buy]; read it rather than assume.
    var mode = (buy && buy.dataset.mode === 'frame') ? 'frame' : 'lens';
    var framePrice = parseInt(root.dataset.framePrice, 10) || 0;
    var frameId = root.dataset.frameId;
    var lens = null;                 // the fetched prescription-lenses product
    var state = { type: 'Single Vision', coating: null, addon: null, tint: '' };

    var $ = function (s) { return root.querySelector(s); };
    var coatSel = $('[data-rx="coating"]');
    var addSel = $('[data-rx="addon"]');
    var tintSel = $('[data-rx="tint"]');
    var tintGrp = $('[data-rx-tintgrp]');
    // Matches both add-ons that contain it: "Photochromatic" and
    // "Photochromatic + blue cut". Read off the VALUE the lens product returns,
    // never off a remembered list -- the client edits these in the admin.
    function wantsTint() { return /photochrom/i.test(state.addon || ''); }

    function needsCoating(addon) {
      return /^(blue cut|photochromatic \+ blue cut)$/i.test(addon || '');
    }
    function hasCoating() {
      return !!state.coating && !/^no coating$/i.test(state.coating);
    }
    function syncAddonCoating() {
      if (!addSel) return;
      Array.prototype.forEach.call(addSel.options, function (o) {
        o.disabled = needsCoating(o.value) && !hasCoating();
      });
      if (needsCoating(state.addon) && !hasCoating()) {
        state.addon = 'None';
        addSel.value = state.addon;
      }
    }

    function optionIndex(name) {
      if (!lens) return -1;
      for (var i = 0; i < lens.options.length; i++) {
        var o = lens.options[i];
        var n = (typeof o === 'string' ? o : o.name) || '';
        if (n.toLowerCase() === name.toLowerCase()) return i;
      }
      return -1;
    }
    function valuesFor(name) {
      var i = optionIndex(name);
      if (i < 0) return [];
      var seen = {}, out = [];
      lens.variants.forEach(function (v) {
        var val = v.options[i];
        if (val && !seen[val]) { seen[val] = 1; out.push(val); }
      });
      return out;
    }
    function findVariant() {
      if (!lens) return null;
      var it = optionIndex('Lens type'), ic = optionIndex('Coating'), ia = optionIndex('Add-on');
      return lens.variants.filter(function (v) {
        return (it < 0 || v.options[it] === state.type)
            && (ic < 0 || v.options[ic] === state.coating)
            && (ia < 0 || v.options[ia] === state.addon);
      })[0] || null;
    }
    // price of a coating on its own, so the bill can show lens and add-on apart
    function basePrice() {
      if (!lens) return 0;
      var it = optionIndex('Lens type'), ic = optionIndex('Coating'), ia = optionIndex('Add-on');
      var none = valuesFor('Add-on')[0];
      var v = lens.variants.filter(function (x) {
        return x.options[it] === state.type && x.options[ic] === state.coating
            && x.options[ia] === none;
      })[0];
      return v ? v.price : 0;
    }

    /* Coating and Add-on are TILES, not dropdowns (client, 2026-08-18: "i don't
       want the coating and add on to appear in drop down but rather than in a
       option selector"). Prices are visible without opening anything, which is
       the real win — a dropdown hides the one number the choice turns on.
       ⚠️ The <select> STAYS as the source of truth, hidden by CSS. Every handler
       already written against it (fillSelect, the change listeners that rebuild
       the dependent Add-on list, state.coating/state.addon, the bill) keeps
       working untouched; the tiles only set .value and fire `change`. Same
       pattern as the power picker. */
    function esc(x) { return String(x).replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;'); }

    function tileBox(sel) {
      if (!sel) return null;
      var box = sel.nextElementSibling;
      if (!box || !box.classList.contains('ycm-tiles')) {
        box = document.createElement('div');
        box.className = 'ycm-tiles';
        sel.parentNode.insertBefore(box, sel.nextSibling);
        box.addEventListener('click', function (e) {
          var b = e.target.closest('button[data-tile]');
          if (!b || b.disabled) return;
          sel.value = b.dataset.tile;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      return box;
    }

    function renderTiles(sel) {
      var box = tileBox(sel);
      if (!box) return;
      box.innerHTML = Array.prototype.filter.call(sel.options, function (o) {
        return o.value !== '';        // a "Select" placeholder is not a tile
      }).map(function (o) {
        var on = o.value === sel.value;
        return '<button type="button" data-tile="' + esc(o.value) + '" ' +
          (o.disabled ? 'disabled style="opacity:.6;cursor:not-allowed" ' : '') +
          'aria-pressed="' + on + '" class="' + (on ? 'is-on' : '') + '">' +
          (o.dataset.swatch
            ? '<i class="ycm-tiles__sw" style="background:' + esc(o.dataset.swatch) + '"></i>'
            : '') +
          '<b>' + esc(o.dataset.label || o.value) + '</b>' +
          (o.dataset.price ? '<span>' + esc(o.dataset.price) + '</span>' : '') +
          (sel === addSel && o.disabled && needsCoating(o.value)
            ? '<span style="font-size:11.5px">Choose a coating to enable this add-on.</span>' : '') +
          '</button>';
      }).join('');
    }

    function paint() {
      syncAddonCoating();
      renderTiles(coatSel); renderTiles(addSel);
      /* ⚠️ CLEAR THE VALUE WHEN THE GROUP GOES AWAY. A tint left behind on a
         non-photochromatic add-on is invisible to the shopper and would still
         be stamped onto the order -- the same trap as a value left in a
         disabled control. */
      var tintOn = wantsTint();
      if (tintGrp) tintGrp.hidden = !tintOn;
      if (tintSel) {
        if (!tintOn && tintSel.value) { tintSel.value = ''; }
        state.tint = tintOn ? tintSel.value : '';
        renderTiles(tintSel);
      }
      var v = findVariant();
      var base = basePrice();
      var addon = v ? Math.max(0, v.price - base) : 0;
      var lensPrice = v ? v.price : 0;
      $('[data-bill="frame"]').textContent = money(framePrice);
      $('[data-bill="lens"]').textContent = money(base);
      $('[data-bill="lenslab"]').textContent =
        state.type.charAt(0) + state.type.slice(1).toLowerCase() + ' · ' +
        (state.coating || '').toLowerCase();
      $('[data-bill="addon"]').textContent = money(addon);
      // an "Add-on  0" row is a line of text that says nothing
      var addonRow = $('[data-bill="addon"]');
      if (addonRow && addonRow.parentNode) addonRow.parentNode.hidden = addon <= 0;
      var billTotal = $('[data-bill="total"]');
      if (billTotal) billTotal.textContent = money(framePrice + lensPrice);
      /* ONE price. The frame+lens total lives in the buy footer, below the
         configurator (client, 2026-08-18: "make the frame + lens pricing appear
         below like it was before as it was even better"), and it NEVER moves
         between modes -- the earlier two-step flow had a total in each step's
         footer, which is what made it jump as the shopper advanced.

         ⚠️ It is now the ONLY price on a prescription-ready frame: the product
         price at the top of the page is not rendered at all on those (see
         sections/ycm-product.liquid). So it shows in BOTH modes -- the frame
         price on its own, or the frame+lens total -- and there is nothing left
         anywhere on the page for it to duplicate. */
      if (buy) {
        var lensMode = mode === 'lens';
        var tot = buy.querySelector('[data-buy-total]');
        var note = buy.querySelector('[data-buy-note]');
        var was = buy.querySelector('[data-buy-was]');
        if (tot) tot.textContent = money(lensMode ? framePrice + lensPrice : framePrice);
        if (note) note.textContent = lensMode ? 'frame + lenses' : 'frame only';
        /* ⚠️ The compare-at is a saving on the FRAME. Left standing beside a
           frame+lens total it reads as a discount on the lenses, which nobody
           offered -- RULE 0. It shows in frame-only mode and hides in lens mode. */
        if (was) was.hidden = lensMode;
      }
      // ⚠️ Add is REMOVED on single vision, not disabled (client, 2026-08-18).
      // A greyed-out row still reads as something the customer failed to fill
      // in; a single-vision lens has no add power at all, so the row should not
      // be on the page. `hidden` also takes it out of the grid, which a
      // disabled input would not.
      var prog = state.type === 'Progressive';
      root.querySelectorAll('[data-rx-addfld]').forEach(function (el) {
        el.hidden = !prog; el.disabled = !prog;
        if (!prog) { el.value = ''; el.classList.remove('is-bad'); }
      });
      var ar = $('[data-rx-addrow]'); if (ar) ar.hidden = !prog;
      syncRxAxis();
    }

    function fillSelect(sel, name, withPrice) {
      if (!sel) return;
      var vals = valuesFor(name);
      sel.innerHTML = '';
      vals.forEach(function (val) {
        var o = document.createElement('option');
        o.value = val;
        if (withPrice) {
          var it = optionIndex('Lens type'), ic = optionIndex('Coating'), ia = optionIndex('Add-on');
          var probe = lens.variants.filter(function (x) {
            return x.options[it] === state.type &&
              (name === 'Coating' ? x.options[ic] === val : x.options[ic] === state.coating) &&
              (name === 'Add-on' ? x.options[ia] === val : x.options[ia] === valuesFor('Add-on')[0]);
          })[0];
          var base = name === 'Add-on' ? basePrice() : 0;
          var delta = probe ? probe.price - base : 0;
          var ptxt = probe ? (name === 'Add-on' && delta > 0 ? '+ ' : '') +
            money(name === 'Add-on' ? delta : probe.price) : '';
          o.dataset.price = ptxt;
          o.textContent = val + (ptxt ? ', ' + ptxt : '');
        } else o.textContent = val;
        o.dataset.label = val;
        sel.appendChild(o);
      });
      return vals;
    }

    function load() {
      if (lens) return Promise.resolve(lens);
      /* ⚠️ ONE FAILED REQUEST USED TO KILL THE PAGE'S ABILITY TO SELL LENSES —
         permanently, with nothing but "please refresh" to show for it. Measured
         2026-09-01: Shopify answered this fetch **HTTP 429** and the whole
         configurator stayed empty for the life of the page. A 429 is not
         exotic — Shopify's own bot protection issues them to real shoppers
         behind shared IPs, and a phone on a flaky connection fails the same
         way. Retry before giving up; a lens sale is the page's whole purpose.

         ⚠️ And note `.then(ok, err)` rather than `.then(ok).catch(err)`: a
         trailing .catch swallows errors thrown by the SUCCESS path too, so a
         render bug in fillSelect/paint would report itself as a network
         failure and send the next debugger to the wrong place entirely. */
      var url = '/products/' + root.dataset.handle + '.js';
      var get = function (left) {
        return fetch(url).then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        }).catch(function (e) {
          if (left <= 0) throw e;
          return new Promise(function (go) { setTimeout(go, 600 * (3 - left)); })
            .then(function () { return get(left - 1); });
        });
      };
      return get(2)
        .then(function (j) {
          lens = j;
          var coats = fillSelect(coatSel, 'Coating', true);
          state.coating = coats[0];
          var addons = fillSelect(addSel, 'Add-on', true);
          state.addon = addons[0];
          fillSelect(coatSel, 'Coating', true);
          coatSel.value = state.coating;
          addSel.value = state.addon;
          paint();
          return lens;
        }, function (e) {
          fail(err, 'Lens options could not be loaded. Please refresh the page.');
          throw e;
        });
    }

    /* ── prescription validation ────────────────────────────────────────── */
    // Spectacles are always a pair, so there is no per-eye on/off any more.
    // Kept as a function so every call site reads the same as the contact lens
    // panel, where one box per eye IS a real choice.
    function rxEyeOn() { return true; }
    /* The axis field is DISABLED and cleared until its own eye has a real
       cylinder — the same treatment the Add row gets on single vision. A
       control that cannot be filled wrongly beats an error message about having
       filled it wrongly, and it is the only version the client can see is
       right just by looking at the page. */
    function syncRxAxis() {
      ['r', 'l'].forEach(function (eye) {
        var cylEl = root.querySelector('[data-rx-pw="cyl"][data-eye="' + eye + '"]');
        var axEl = root.querySelector('[data-rx-pw="axis"][data-eye="' + eye + '"]');
        if (!cylEl || !axEl) return;
        var on = cylIsReal(cylEl);
        axEl.disabled = !on;
        axEl.classList.toggle('is-off', !on);
        axEl.placeholder = on ? '1-180' : '--';
        if (!on) { axEl.value = ''; axEl.classList.remove('is-bad'); }
      });
    }

    /* ⚠️ Say it AS THEY TYPE, not when they press buy (client, 2026-08-18:
       "the axis error ... should appear right when someone enters it not when
       they try to buy with it"). An axis of 200 is wrong the moment the second
       0 lands; making someone finish the form and press a button to find out is
       the worst possible moment to mention it. */
    function liveAxis(el) {
      var raw = (el.value || '').trim();
      if (!raw) { el.classList.remove('is-bad'); clearErr(rxErr); return; }
      if (normAxis(raw) === null) {
        el.classList.add('is-bad');
        fail(rxErr, 'Axis is a whole number from 1 to 180.');
      } else {
        el.classList.remove('is-bad');
        clearErr(rxErr);
      }
    }

    /* ⚠️ "Same in both eyes" IS BIDIRECTIONAL. It used to copy right to left
       only, and only on `input` -- so a shopper who filled the LEFT column saw
       nothing happen in either direction, and ticking the box afterwards wiped
       what they had typed by copying an empty right column over it (client,
       2026-08-19). Mirror from whichever eye was actually touched, and re-run
       the axis rule afterwards because the cylinder that just arrived decides
       whether the other eye's axis exists at all. */
    var RX_KEYS = ['sph', 'cyl', 'axis', 'add'];
    function rxSameOn() {
      var c = root.querySelector('[data-rx-same]');
      return !!(c && c.checked);
    }
    function rxField(k, eye) {
      return root.querySelector('[data-rx-pw="' + k + '"][data-eye="' + eye + '"]');
    }
    function mirrorRx(src) {
      if (!rxSameOn() || !src || !src.dataset.rxPw) return;
      var dst = rxField(src.dataset.rxPw, src.dataset.eye === 'r' ? 'l' : 'r');
      if (!dst) return;
      dst.value = src.value;
      dst.classList.add('is-mirror');
      dst.classList.remove('is-bad');
      syncRxAxis();
    }
    function mirrorAllRx() {
      // copy from the eye the shopper has actually filled in; right wins a tie
      var from = RX_KEYS.some(function (k) { var e = rxField(k, 'r'); return e && e.value; }) ? 'r' : 'l';
      var to = from === 'r' ? 'l' : 'r';
      RX_KEYS.forEach(function (k) {
        var a = rxField(k, from), b = rxField(k, to);
        if (!a || !b) return;
        b.value = a.value;
        b.classList.add('is-mirror');
        b.classList.remove('is-bad');
      });
      syncRxAxis();
    }
    function unmirrorRx() {
      RX_KEYS.forEach(function (k) {
        ['r', 'l'].forEach(function (eye) {
          var el = rxField(k, eye); if (el) el.classList.remove('is-mirror');
        });
      });
    }

    function syncRxEyes() {
      var prog = state.type === 'Progressive';
      root.querySelectorAll('[data-rx-pw][data-eye]').forEach(function (el) {
        var isAdd = el.dataset.rxPw === 'add';
        el.disabled = isAdd && !prog;
        el.hidden = isAdd && !prog;
      });
    }
    function rxMode() {
      var m = root.querySelector('input[name^="rxmode-"]:checked');
      return m ? m.value : 'manual';
    }
    function validateRx() {
      clearErr(rxErr); clearErr(err);
      if (needsCoating(state.addon) && !hasCoating()) {
        return { ok: fail(err, 'Choose a coating to enable this add-on.') };
      }
      /* ⚠️ Ahead of the upload branch on purpose: an uploaded prescription
         needs the tint just as much as a typed one, and that branch returns
         early. */
      if (wantsTint() && !state.tint) {
        /* fail()'s field argument focuses the element and marks it .is-bad --
           useless here, because the <select> is display:none (it is only the
           tiles' source of truth). Mark and scroll the TILE BOX instead. */
        var tbox = tintSel && tintSel.nextElementSibling;
        if (tbox) {
          tbox.classList.add('is-bad');
          try { tbox.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
        }
        return { ok: fail(rxErr, 'Choose a photochromatic tint — brown or grey.') };
      }
      var mode = rxMode();
      if (mode === 'upload') {
        var f = root.querySelector('[data-rx-file]');
        if (!f || !f.files || !f.files.length) {
          return { ok: fail(rxErr, 'Please choose your prescription file, or switch to ' +
                                   '“Enter it here”.', f) };
        }
        return { ok: true, props: { 'Prescription': 'Uploaded: ' + f.files[0].name } };
      }
      var props = {}, bad = null, msg = '';
      ['r', 'l'].forEach(function (eye) {
        if (bad || !rxEyeOn(eye)) return;
        var label = eye === 'r' ? 'Right eye' : 'Left eye';
        var get = function (k) {
          return root.querySelector('[data-rx-pw="' + k + '"][data-eye="' + eye + '"]');
        };
        var sphEl = get('sph'), cylEl = get('cyl'), axEl = get('axis'), addEl = get('add');

        var sph = normPower(sphEl.value);
        if (!(sphEl.value || '').trim()) {
          bad = sphEl; msg = label + ': enter the power (SPH).'; return;
        }
        if (sph === null) {
          bad = sphEl; msg = label + ': “' + sphEl.value + '” is not a power. Use e.g. -2.25.';
          return;
        }
        var parts = ['SPH ' + sph];

        /* rule 3, correctly this time: the AXIS belongs to the CYLINDER.
           A cylinder of 0.00 is a real selection meaning "no astigmatism", so
           it takes no axis at all — and a cylinder that is real takes one
           always. See cylIsReal() at the top of this file. */
        var cylRaw = (cylEl.value || '').trim(), axRaw = (axEl.value || '').trim();
        if (cylRaw) {
          var cyl = normPower(cylRaw);
          if (cyl === null) { bad = cylEl; msg = label + ': “' + cylRaw + '” is not a cylinder value.'; return; }
          if (parseFloat(cyl) !== 0) {
            if (!axRaw) { bad = axEl; msg = label + ': you gave a cylinder, so the axis is needed too.'; return; }
            var ax = normAxis(axRaw);
            if (ax === null) { bad = axEl; msg = label + ': axis is a whole number from 1 to 180.'; return; }
            parts.push('CYL ' + cyl, 'Axis ' + ax);
          } else if (axRaw) {
            bad = axEl; msg = label + ': an axis needs a cylinder. Choose a cylinder, or clear the axis.'; return;
          }
        } else if (axRaw) {
          bad = cylEl; msg = label + ': you gave an axis, so the cylinder is needed too.'; return;
        }
        if (state.type === 'Progressive') {
          var addRaw = (addEl.value || '').trim();
          if (!addRaw) { bad = addEl; msg = label + ': a progressive lens needs an Add power.'; return; }
          var addv = normPower(addRaw);
          if (addv === null) { bad = addEl; msg = label + ': “' + addRaw + '” is not an Add power.'; return; }
          if (parseFloat(addv) <= 0) { bad = addEl; msg = label + ': Add power is a plus number, e.g. +2.00.'; return; }
          parts.push('ADD ' + addv);
        }
        props[label] = parts.join('  ');
      });
      if (bad) return { ok: fail(rxErr, msg, bad) };
      return { ok: true, props: props };
    }

    /* ── wire ───────────────────────────────────────────────────────────── */
    root.querySelectorAll('[data-rx="type"]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.type = b.dataset.val;
        root.querySelectorAll('[data-rx="type"]').forEach(function (x) {
          x.setAttribute('aria-pressed', String(x === b));
        });
        var keepC = state.coating, keepA = state.addon;
        fillSelect(coatSel, 'Coating', true);
        if (valuesFor('Coating').indexOf(keepC) >= 0) coatSel.value = keepC;
        state.coating = coatSel.value;
        fillSelect(addSel, 'Add-on', true);
        if (valuesFor('Add-on').indexOf(keepA) >= 0) addSel.value = keepA;
        state.addon = addSel.value;
        paint(); syncRxEyes();
      });
    });
    if (coatSel) coatSel.addEventListener('change', function () {
      state.coating = coatSel.value; fillSelect(addSel, 'Add-on', true);
      addSel.value = state.addon; paint();
    });
    if (addSel) addSel.addEventListener('change', function () {
      state.addon = addSel.value; paint();
    });
    if (tintSel) tintSel.addEventListener('change', function () {
      state.tint = tintSel.value;
      var box = tintSel.nextElementSibling;
      if (box) box.classList.remove('is-bad');
      clearErr(rxErr);
      paint();
    });
    root.addEventListener('change', function (e) {
      // ⚠️ the axis follows its own cylinder, so it has to be re-synced on
      // every cylinder change -- not only from paint(), which the coating and
      // add-on drive but a power field never does. Without this the field was
      // disabled at load and stayed that way for ever, which is a WORSE bug
      // than the one it was fixing: an unfillable axis on a real astigmatism.
      if (e.target.matches('[data-rx-pw="cyl"]')) syncRxAxis();
      if (e.target.matches('input[name^="rxmode-"]')) {
        var m = e.target.value;
        root.querySelectorAll('[data-rx-pane]').forEach(function (p) {
          p.hidden = p.dataset.rxPane !== m;
        });
        clearErr(err);
      }
      if (e.target.matches('[data-rxeye-on]')) syncRxEyes();
      if (e.target.matches('[data-rx-same]')) {
        if (e.target.checked) mirrorAllRx(); else unmirrorRx();
      } else if (e.target.matches('[data-rx-pw]')) {
        mirrorRx(e.target);
      }
    });
    root.addEventListener('input', function (e) {
      if (e.target.matches('[data-rx-pw="axis"]')) liveAxis(e.target);
      if (e.target.matches('[data-rx-pw]')) mirrorRx(e.target);
    });

    /* ── the two buttons, and the switch that tells them what to buy ──────
       Approach A. `mode` is the ONLY thing that changes what gets added; both
       buttons run the identical code and differ by where they send the shopper.
       That is what keeps four purchase paths behind two controls. */
    function setMode(m) {
      mode = m;
      if (buy) {
        buy.dataset.mode = m;
        buy.querySelectorAll('[data-buymode]').forEach(function (x) {
          x.setAttribute('aria-pressed', String(x.dataset.buymode === m));
        });
        var cfg = buy.querySelector('[data-buy-config]');
        if (cfg) cfg.hidden = m !== 'lens';
      }
      clearErr(err);
      paint();
    }

    if (buy) {
      buy.querySelectorAll('[data-buymode]').forEach(function (b) {
        b.addEventListener('click', function () { setMode(b.dataset.buymode); });
      });
    }

    function wantedQty() {
      var i = buy && buy.querySelector('.ycm-qty input');
      return Math.max(1, parseInt(i && i.value, 10) || 1);
    }

    function purchase(btn, toCheckout) {
      clearErr(err);
      var n = wantedQty(), items;

      if (mode === 'frame') {
        items = [{ id: Number(frameId), quantity: n }];
      } else {
        var res = validateRx();
        if (!res.ok) return;                      // validateRx already reported
        var v = findVariant();
        if (!v) { fail(err, 'Please choose a lens type and coating.'); return; }
        /* ⚠ The pair token is what makes the lens INSEPARABLE from its frame:
           assets/ycm.js drops any lens line whose partner has been removed from
           the cart. Underscore-prefixed, so Shopify stores it on the order but
           never shows it to the shopper. Same idea as BabyRoo's embroidery. */
        var tok = 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        var props = Object.assign({
          'Lens': state.type + ' · ' + state.coating +
                  (state.addon && !/^none$/i.test(state.addon) ? ' · ' + state.addon : ''),
          'For frame': root.dataset.frameTitle,
          '_ycm_pair': tok
        }, res.props);
        // Its own line on the order, not folded into `Lens`: the lab reads this
        // one to decide which tint to make, so it must be unmissable.
        if (wantsTint() && state.tint) props['Photochromatic tint'] = state.tint;
        items = [{ id: Number(frameId), quantity: n, properties: { '_ycm_pair': tok } },
                 { id: v.id, quantity: n, properties: props }];
      }

      btn.disabled = true;
      var oops = function (e) {
        btn.disabled = false;
        fail(err, (e && e.message) || 'Could not add to cart. Please try again.');
      };

      if (toCheckout) {
        checkoutWithOnly(function () {
          return jcart(cartRoot() + 'cart/add.js', { items: items });
        }).catch(oops);
        return;
      }

      jcart('/cart/add.js', { items: items }).then(function () {
        btn.disabled = false;
        /* ⚠️ THE DRAWER DOES NOT RE-RENDER ITSELF. We post our own two-line
           /cart/add.js (Dawn's product form cannot express a frame plus a lens
           carrying properties), so Dawn's cart-drawer.js never sees the add and
           its markup stays exactly as the page loaded — i.e. "Your cart is
           empty" — over a cart that is actually correct.

           ycmRefreshCart refetches Dawn's own cart-drawer and cart-icon-bubble
           sections, drops .is-empty and opens the drawer itself, so there is
           nothing to open here.

           This previously called `window.ycmAddToCart.refresh`, which is
           undefined — ycmAddToCart is a function, not an object with a refresh
           method — so the whole line was dead and every shopper who added a
           prescription frame was shown an empty cart. */
        if (window.ycmRefreshCart) return window.ycmRefreshCart();
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
        else window.location = '/cart';
      }).catch(oops);
    }

    if (buy) {
      buy.querySelectorAll('[data-buy]').forEach(function (b) {
        b.addEventListener('click', function () {
          purchase(b, b.dataset.buy === 'now');
        });
      });
    }

    // The panel is visible from the start now (Frame + lenses is the default),
    // so the lens product has to be fetched on init rather than on a click.
    // ⚠️ Not a bare swallow. A configurator that fails to populate is the one
    // failure that stops this page selling anything, so it says so out loud.
    load().catch(function (e) {
      if (window.console) console.error('[ycm] lens options failed to load:', e);
    });
  }

  /* ── the power picker ─────────────────────────────────────────────────────
     Client, 2026-08-18: the native <select> picker is whatever the device
     decides, and on a phone that is a cramped scrolling wheel with 58 dioptre
     values in it. This replaces it with a sheet that splits the list into
     (+) Positive and (-) Negative columns, the way Lenskart does.

     ⚠️ THE <select> IS NOT REPLACED, ONLY INTERCEPTED. It stays in the DOM,
     visible, and remains the single source of truth — so `is-bad`, `is-off`,
     `disabled`, `hidden`, the mirror logic, the totals and the cart-property
     serialisation all keep working untouched. Hiding it behind a custom button
     would have meant re-implementing every one of those, and a validation error
     would have had nothing to highlight. The only thing taken away is the
     browser's own dropdown, via preventDefault on mousedown/keydown; if that
     ever fails on some browser the user simply gets the native list back, which
     is a working fallback rather than a broken field.
  ───────────────────────────────────────────────────────────────────────────*/
  var PICK_LABEL = {
    sph: 'Spherical', cyl: 'Cylindrical', axis: 'Axis',
    add: 'Additional power', boxes: 'Number of boxes'
  };
  var picker = null;

  function pickerEl() {
    if (picker) return picker;
    picker = document.createElement('div');
    picker.className = 'ycm-pick';
    picker.hidden = true;
    picker.innerHTML =
      '<div class="ycm-pick__sheet" role="dialog" aria-modal="true" aria-labelledby="ycm-pick-t">' +
        '<div class="ycm-pick__hd">' +
          '<h3 id="ycm-pick-t"></h3>' +
          '<button type="button" class="ycm-pick__clear" hidden>Clear</button>' +
          '<button type="button" class="ycm-pick__x" aria-label="Close">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
            'stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="ycm-pick__cols"></div>' +
      '</div>';
    document.body.appendChild(picker);

    picker.addEventListener('click', function (e) {
      if (e.target === picker || e.target.closest('.ycm-pick__x')) return closePick();
      if (e.target.closest('.ycm-pick__clear')) return choose('');
      var row = e.target.closest('[data-pick-val]');
      if (row) choose(row.dataset.pickVal);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !picker.hidden) closePick();
    });
    return picker;
  }

  var pickTarget = null, pickPrevFocus = null;

  function choose(val) {
    if (!pickTarget) return;
    pickTarget.value = val;
    pickTarget.dispatchEvent(new Event('input', { bubbles: true }));
    pickTarget.dispatchEvent(new Event('change', { bubbles: true }));
    closePick();
  }

  function closePick() {
    if (!picker || picker.hidden) return;
    picker.hidden = true;
    document.documentElement.style.overflow = '';
    if (pickPrevFocus) { try { pickPrevFocus.focus(); } catch (err) {} }
    pickTarget = null;
  }

  function openPick(sel) {
    var el = pickerEl();
    pickTarget = sel;
    pickPrevFocus = sel;

    var key = sel.dataset.rxPw || sel.dataset.pw || '';
    var name = sel.dataset.pick || PICK_LABEL[key] || 'Select a value';
    var eye = sel.dataset.eye === 'r' ? 'Right Eye'
            : sel.dataset.eye === 'l' ? 'Left Eye' : '';
    el.querySelector('#ycm-pick-t').textContent = eye ? name + ' | ' + eye : name;

    var opts = Array.prototype.slice.call(sel.options)
      .filter(function (o) { return o.value !== ''; });
    var hasBlank = Array.prototype.some.call(sel.options, function (o) { return o.value === ''; });
    var clear = el.querySelector('.ycm-pick__clear');
    clear.hidden = !(hasBlank && sel.value !== '');

    // A list is only split when it actually carries signs. "1..6" boxes must
    // not be forced into a (+)/(-) layout.
    var pos = opts.filter(function (o) { return o.value.charAt(0) !== '-'; });
    var neg = opts.filter(function (o) { return o.value.charAt(0) === '-'; });
    // ⚠️ The negative column counts AWAY FROM ZERO: -0.25, -0.50, … -8.50. The
    // <option> order is numerically ascending, so straight off the select it
    // starts at -8.50 — the strongest power first, which is both the rarest and
    // the opposite of how the positive column beside it reads.
    neg.reverse();
    var split = pos.length > 0 && neg.length > 0;

    function col(list, head) {
      if (!list.length) return '';
      return '<div class="ycm-pick__col">' +
        (head ? '<div class="ycm-pick__ch">' + head + '</div>' : '') +
        '<div class="ycm-pick__list">' + list.map(function (o) {
          var on = o.value === sel.value;
          return '<button type="button" class="ycm-pick__row' + (on ? ' is-on' : '') + '" ' +
                 'data-pick-val="' + o.value.replace(/"/g, '&quot;') + '" ' +
                 'role="radio" aria-checked="' + (on ? 'true' : 'false') + '">' +
                 '<span class="ycm-pick__dot"></span>' + o.textContent + '</button>';
        }).join('') + '</div></div>';
    }
    el.querySelector('.ycm-pick__cols').innerHTML = split
      ? col(pos, '(+) Positive') + col(neg, '(&minus;) Negative')
      : col(opts, '');
    el.querySelector('.ycm-pick__sheet').classList.toggle('is-split', split);

    el.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    var on = el.querySelector('.ycm-pick__row.is-on') || el.querySelector('.ycm-pick__row');
    if (on) { on.scrollIntoView({ block: 'center' }); on.focus({ preventScroll: true }); }
  }

  /* A power <select> with no values in it is an UNORDERABLE PRODUCT — the
     customer opens the picker, finds an empty list, and cannot buy. That is
     exactly what a missing or emptied power_profile metaobject produces, and it
     is one admin click away. Falling back to a typed box keeps the page working:
     normPower() validates typed input just as strictly, so nothing downstream
     changes. Never leave the empty dropdown on the page. */
  function emptyToInput(sel) {
    if (sel.options.length > 1 || (sel.dataset.pw || '') === 'boxes') return sel;
    var i = document.createElement('input');
    i.type = 'text';
    i.inputMode = (sel.dataset.rxPw || sel.dataset.pw) === 'axis' ? 'numeric' : 'decimal';
    i.placeholder = 'Enter';
    i.className = sel.className;
    ['rxPw', 'pw', 'eye', 'pick', 'rxAddfld'].forEach(function (k) {
      if (sel.dataset[k] !== undefined) i.dataset[k] = sel.dataset[k];
    });
    if (sel.getAttribute('aria-label')) i.setAttribute('aria-label', sel.getAttribute('aria-label'));
    sel.parentNode.replaceChild(i, sel);
    return i;
  }

  function initPicker(scope) {
    scope.querySelectorAll('.ycm-eyes select').forEach(function (node) {
      var sel = emptyToInput(node);
      if (sel.tagName !== 'SELECT') return;
      if (sel.dataset.pickBound) return;
      sel.dataset.pickBound = '1';
      sel.addEventListener('mousedown', function (e) {
        if (sel.disabled) return;
        e.preventDefault();
        openPick(sel);
      });
      sel.addEventListener('keydown', function (e) {
        if (sel.disabled) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          openPick(sel);
        }
      });
    });
  }

  /* ── boot ────────────────────────────────────────────────────────────── */
  /* ⚠ AXIS IS A WHOLE NUMBER OF DEGREES AND NOTHING ELSE.
     `inputmode="numeric"` only chooses which keyboard a phone offers -- it does
     not stop a desktop keyboard, a paste, or an Android suggestion strip, so
     "i am able to enter letters and other things in the axis" (client,
     2026-08-18) was exactly right. validateRx/validate already REJECT a bad
     axis, but rejecting after the fact is not the same as never accepting it.
     One delegated listener covers both lens panels, the pickers' typed
     fallback inputs, and any axis field added later. */
  /* ⚠️ THE `true` IS load-BEARING -- it is the CAPTURE phase, and without it
     the filter runs too LATE to be worth having.

     `input` bubbles, so a listener bound on the panel (the mirror that copies a
     value into the other eye) sits closer to the target than this one on
     `document` and therefore fires FIRST. The mirror copied the raw keystroke
     to the other column, and only then did this handler strip it from the field
     the shopper was typing in -- so with "Same in both eyes" ticked, a typed
     letter was scrubbed from the right eye and left sitting in the left one
     (client, 2026-08-19). It looked like the filter was broken; it was simply
     second in the queue.

     Sanitising in the capture phase means EVERY other listener -- mirroring,
     live validation, totals, anything added later -- reads an already-clean
     value, without any of them needing to know the field is numeric. */
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.matches ||
        !t.matches('input[data-rx-pw="axis"],input[data-pw="axis"]')) return;
    var clean = t.value.replace(/[^0-9]/g, '').slice(0, 3);
    if (clean !== t.value) t.value = clean;
  }, true);

  /* -- "Buy it now" on products without a prescription step (sunglasses and the
     like; user, 2026-09-29): the theme's own button instead of Razorpay's branded
     one. The variant is read from the product form at click time. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ycm-now]');
    if (!btn || btn.disabled) return;
    var form = document.getElementById(btn.dataset.form);
    var id = form && form.querySelector('input[name="id"]');
    var qf = document.getElementById(btn.dataset.qtyField);
    var err = btn.parentElement.querySelector('[data-ycm-now-err]');
    if (!id || !id.value) return;
    var qty = Math.max(1, parseInt(qf && qf.value, 10) || 1);
    var label = btn.textContent;
    btn.disabled = true; btn.setAttribute('aria-busy', 'true'); clearErr(err);
    checkoutWithOnly(function () {
      return jcart(cartRoot() + 'cart/add.js', { items: [{ id: Number(id.value), quantity: qty }] });
    }).catch(function (x) {
      if (err) { err.textContent = (x && x.message) || 'Something went wrong. Please try again.'; err.hidden = false; }
    }).then(function () {
      btn.disabled = false; btn.removeAttribute('aria-busy'); btn.textContent = label;
    });
  });

  function boot() {
    var panel = document.querySelector('[data-ycm-power]');
    if (panel) { initPower(panel); guardForm(panel); initPicker(panel); }
    var rx = document.querySelector('[data-ycm-rx]');
    if (rx) { initRx(rx); initPicker(rx); }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else boot();
})();
